import React from 'react';
import { inCurrentTree } from '../design/previewPath';
import styled, { keyframes } from 'styled-components';
import { APP_NOTICE_Z_INDEX } from '../styles/layers';
// The message body is model output stored verbatim by send_platform_message_to_user
// (functions/src/index.ts writes content.html = the planner's message_content),
// so it renders through the same sanitizer the chat bubbles use — security
// remediation slice 4 (2026-09-15, docs/PLAN-security-remediation-20260915.md §2.6).
import { sanitizeHtmlInput } from './GlobalChatSystem/utils';
import { onSnapshot, query, orderBy, Unsubscribe, deleteDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { Paths } from '../repositories/firestoreUtils';
import { useNavigate } from 'react-router-dom';
import { followProvidedLink } from '../lib/linkLanding';

type MessageType = 'notification' | 'alert' | 'info' | 'warning' | 'error' | 'success' | 'system';
type PriorityLevel = 'urgent' | 'high' | 'normal' | 'low';

type ActionButton = { button_text: string; action_type: string; action_value: string; button_style: string };

type MessageDoc = {
  message_id: string;
  user_id: string;
  message_type: MessageType;
  priority_level: PriorityLevel;
  created_at_iso?: string;
  expires_at_iso?: string | null;
  content?: { markdown?: string; html?: string };
  action_buttons?: ActionButton[];
  read?: boolean;
  in_app?: { status?: string; delivered_at_iso?: string };
};

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const OverlayContainer = styled.div`
  position: fixed;
  left: 50%;
  transform: translateX(-50%);
  bottom: calc((var(--prompt-bar-height, 56px)) + 12px + env(safe-area-inset-bottom, 0px));
  width: 90%;
  max-width: 800px;
  /* Above the chat in every mode (owner decision 2026-09-11): a platform
     notice parked behind the full-screen takeover never reaches someone
     who lives in the chat. Below the modal rung, so it never covers a
     dialog the user is answering. */
  z-index: ${APP_NOTICE_Z_INDEX};
`;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 40vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

const MessageCard = styled.div<{ variant: MessageType; priority: PriorityLevel }>`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid rgba(0,0,0,0.08);
  background: white;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  animation: ${slideUp} 200ms ease-out;

  ${props => props.variant === 'info' ? 'border-left: 3px solid #3B82F6' : ''};
  ${props => props.variant === 'success' ? 'border-left: 3px solid #10B981' : ''};
  ${props => props.variant === 'warning' ? 'border-left: 3px solid #F59E0B' : ''};
  ${props => props.variant === 'error' ? 'border-left: 3px solid #EF4444' : ''};
  ${props => props.variant === 'alert' ? 'border-left: 3px solid #DC2626' : ''};
  ${props => props.variant === 'notification' ? 'border-left: 3px solid #6B7280' : ''};
  ${props => props.variant === 'system' ? 'border-left: 3px solid #111827' : ''};
`;

const Icon = styled.div`
  width: 20px;
  height: 20px;
  margin-top: 2px;
`;

const Content = styled.div`
  flex: 1;
  color: var(--brand-text);
  font-size: 0.95rem;
  line-height: 1.35;

  a { color: var(--brand-text); text-decoration: underline; }
  .mention { background: #F3F4F6; padding: 0 4px; border-radius: 4px; }
`;

const Actions = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const ActionBtn = styled.button<{ styleType: string }>`
  padding: 6px 10px;
  border-radius: 6px;
  border: 1px solid #E5E7EB;
  background: ${p => p.styleType === 'primary' ? 'var(--brand-text)' : (p.styleType === 'destructive' ? '#FEE2E2' : '#fff')};
  color: ${p => p.styleType === 'primary' ? '#fff' : 'var(--brand-text)'};
  font-size: 0.85rem;
  cursor: pointer;
`;

const InlineForm = styled.form`
  display: flex;
  gap: 6px;
  margin-top: 6px;
  input, textarea { border: 1px solid #E5E7EB; border-radius: 6px; padding: 6px 8px; font-size: 0.9rem; }
  textarea { min-height: 56px; }
`;

function useCurrentUid(): string | null {
  const [uid, setUid] = React.useState<string | null>(null);
  React.useEffect(() => {
    const auth = getAuth();
    const cur = auth.currentUser?.uid || null;
    setUid(cur);
    const unsub = auth.onAuthStateChanged((u) => setUid(u?.uid || null));
    return () => { try { unsub(); } catch {} };
  }, []);
  return uid;
}

function isExpired(item: MessageDoc): boolean {
  if (!item?.expires_at_iso) return false;
  const t = Date.parse(item.expires_at_iso);
  if (Number.isNaN(t)) return false;
  return Date.now() > t;
}

export const InAppMessageOverlay: React.FC = () => {
  const uid = useCurrentUid();
  const [items, setItems] = React.useState<MessageDoc[]>([]);
  const navigate = useNavigate();
  const stackRef = React.useRef<HTMLDivElement>(null);
  const [activeForm, setActiveForm] = React.useState<{ messageId: string; spec: string } | null>(null);
  const [formValue, setFormValue] = React.useState<string>('');

  // Realtime listener
  React.useEffect(() => {
    if (!uid) return;
    const col = Paths.userPlatformMessagesCol(uid);
    const qy = query(col, orderBy('created_at', 'asc'));
    let unsub: Unsubscribe | undefined;
    // PRE-70: Track dispatched message IDs to avoid re-dispatching the full collection on every snapshot.
    // Without this, switching threads causes all historical messages to re-dispatch and bleed into the new thread.
    const dispatchedIds = new Set<string>();
    let isFirstSnapshot = true;
    unsub = onSnapshot(qy, (snap) => {
      const next: MessageDoc[] = snap.docs.map(d => ({ message_id: d.id, ...(d.data() as any) }));
      const filtered = next
        .filter(x => !isExpired(x))
        .filter(x => String((x as any)?.source_tool || '') === 'send_platform_message_to_user')
        .filter(x => !String((x as any)?.content?.markdown || '').toLowerCase().includes('we could not reach the planning service'));
      setItems(filtered);
      // PRE-70: Only dispatch NEW messages (not yet dispatched) into the chat thread.
      // On first snapshot, mark all existing messages as "already dispatched" — localStorage restore
      // handles historical messages, so we only want truly new arrivals dispatched as events.
      try {
        if (isFirstSnapshot) {
          filtered.forEach(m => dispatchedIds.add(m.message_id));
          isFirstSnapshot = false;
        } else {
          const newMessages = filtered.filter(m => !dispatchedIds.has(m.message_id));
          if (newMessages.length > 0) {
            newMessages.forEach(m => dispatchedIds.add(m.message_id));
            // PRE-70: Skip dispatching session-tagged messages — LandingPage has its own
            // session-scoped Firestore listener that handles those directly. Only dispatch
            // untagged messages (non-workflow) through this broadcast path.
            const untaggedMessages = newMessages.filter((m) => !(m as any)?.metadata?.session_id);
            if (untaggedMessages.length > 0) {
              const chatItems = untaggedMessages.map((m) => ({ text: String((m as any)?.content?.markdown || ''), html: String((m as any)?.content?.html || ''), isUser: false, timestamp: new Date(), session_id: undefined }));
              window.dispatchEvent(new CustomEvent('chat:add-platform-messages', { detail: chatItems }));
            }
          }
        }
      } catch {}
      // Mark delivered for new messages
      const nowIso = new Date().toISOString();
      filtered.forEach(async (m) => {
        if (!(m as any)?.in_app?.delivered_at_iso) {
          try { await setDoc(Paths.userPlatformMessageDoc(uid, m.message_id), { in_app: { status: 'DELIVERED', delivered_at: serverTimestamp(), delivered_at_iso: nowIso } }, { merge: true }); } catch {}
          try { await setDoc(Paths.userPlatformMessageEventDoc(uid, m.message_id, `${Date.now()}`), { event: 'delivered', channel: 'in_app', ts: nowIso }); } catch {}
        }
      });
    }, (err) => {
      // eslint-disable-next-line no-console
      console.warn('InAppMessageOverlay listener error:', err?.message || err);
    });
    return () => { try { unsub && unsub(); } catch {} };
  }, [uid]);

  // Fallback polling of API queue so chat receives messages even without Firestore/Admin
  React.useEffect(() => {
    let active = true;
    async function poll() {
      if (!active || !uid) return;
      try {
        const host = (window.location.hostname || '').toLowerCase();
        const isLocal = host === 'localhost' || host === '127.0.0.1' || host.endsWith('.local');
        if (!isLocal) return; // Production uses Firestore real-time listeners
        const res = await fetch(`http://127.0.0.1:3001/api/pull-inapp-messages?uid=${encodeURIComponent(uid)}`);
        const data = await res.json().catch(() => ({ ok: false }));
        if (data && data.ok && Array.isArray(data.items) && data.items.length) {
          const chatItems = data.items.map((it: any) => ({ text: String(it?.text || ''), html: String(it?.html || ''), isUser: false, timestamp: new Date() }));
          try { window.dispatchEvent(new CustomEvent('chat:add-platform-messages', { detail: chatItems })); } catch {}
        }
      } catch {}
      if (active) setTimeout(poll, 1500);
    }
    poll();
    return () => { active = false; };
  }, [uid]);

  // Auto-scroll to newest
  React.useEffect(() => {
    try {
      const el = stackRef.current;
      if (el) { el.scrollTop = el.scrollHeight; }
    } catch {}
  }, [items.length]);

  // Expiration cleanup timer
  React.useEffect(() => {
    if (!uid) return;
    const t = window.setInterval(async () => {
      const expired = items.filter(isExpired);
      for (const m of expired) {
        try { await deleteDoc(Paths.userPlatformMessageDoc(uid, m.message_id)); } catch {}
      }
    }, 60 * 1000);
    return () => { window.clearInterval(t); };
  }, [uid, items]);

  const handleAction = async (m: MessageDoc, btn: ActionButton) => {
    const kind = String(btn.action_type || '').toLowerCase();
    const val = String(btn.action_value || '');
    try {
      // Log event
      const logRef = Paths.userActionEventDoc(m.user_id, `${m.message_id}:${Date.now()}`);
      try { await setDoc(logRef, { message_id: m.message_id, action_type: kind, action_value: val, ts: new Date().toISOString() }, { merge: false }); } catch {}

      switch (kind) {
        // a provided link lands on its page (src/lib/linkLanding.ts); what it does not take stays as it was
        case 'navigate':
        case 'url':
          if (!followProvidedLink(val)) navigate(val);
          break;
        case 'open_url':
          if (!followProvidedLink(val, {}, { target: '_blank' })) window.open(val, '_blank', 'noopener,noreferrer');
          break;
        case 'deep_link':
          window.location.href = inCurrentTree(val);
          break;
        case 'copy_text':
          await navigator.clipboard.writeText(val);
          break;
        case 'dismiss':
          await deleteDoc(Paths.userPlatformMessageDoc(m.user_id, m.message_id));
          break;
        case 'confirm': {
          const ok = window.confirm('are you sure?');
          if (ok) {
            const reqId = `${m.message_id}:confirm:${Date.now()}`;
            await setDoc(Paths.userActionRequestDoc(m.user_id, reqId), { message_id: m.message_id, action_type: 'confirm', action_value: val, requested_at: new Date().toISOString(), state: 'REQUESTED' });
          }
          break;
        }
        case 'form':
          setActiveForm({ messageId: m.message_id, spec: val });
          setFormValue('');
          break;
        case 'callback':
        case 'custom': {
          // Enqueue action request for backend processing/tools
          const reqId = `${m.message_id}:${kind}:${Date.now()}`;
          await setDoc(Paths.userActionRequestDoc(m.user_id, reqId), { message_id: m.message_id, action_type: kind, action_value: val, requested_at: new Date().toISOString(), state: 'REQUESTED' });
          break;
        }
        default:
          break;
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Action failed', e);
    }
  };

  const handleSubmitForm = async (e: React.FormEvent) => {
    e.preventDefault();
    const af = activeForm; if (!af || !uid) return;
    const reqId = `${af.messageId}:form:${Date.now()}`;
    try {
      await setDoc(Paths.userActionRequestDoc(uid, reqId), { message_id: af.messageId, action_type: 'form', action_value: af.spec, form_value: formValue, requested_at: new Date().toISOString(), state: 'REQUESTED' });
      setActiveForm(null);
      setFormValue('');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Form submit failed', e);
    }
  };

  if (!uid || items.length === 0) return null;

  return (
    <OverlayContainer>
      <Stack ref={stackRef}>
        {items.map((m) => (
          <MessageCard key={m.message_id} variant={m.message_type} priority={m.priority_level}>
            <Icon aria-hidden="true">{/* icon may be swapped for lucide icon if desired */}
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.75">
                <circle cx="12" cy="12" r="10" />
              </svg>
            </Icon>
            <Content
              onMouseEnter={async () => { try { await setDoc(Paths.userPlatformMessageEventDoc(m.user_id, m.message_id, `${Date.now()}`), { event: 'view', ts: new Date().toISOString() }); } catch {} }}
              dangerouslySetInnerHTML={{ __html: sanitizeHtmlInput(String(m?.content?.html || m?.content?.markdown || '')) }}
            />
            <Actions>
              {(m.action_buttons || []).map((b, idx) => (
                <ActionBtn key={idx} styleType={b.button_style} onClick={() => handleAction(m, b)}>
                  {b.button_text}
                </ActionBtn>
              ))}
            </Actions>
            {activeForm && activeForm.messageId === m.message_id && (
              <InlineForm onSubmit={handleSubmitForm}>
                <textarea value={formValue} onChange={(ev) => setFormValue(ev.target.value)} placeholder="enter details" />
                <ActionBtn styleType={'primary'} as="button" type="submit">submit</ActionBtn>
                <ActionBtn styleType={'secondary'} as="button" type="button" onClick={() => setActiveForm(null)}>cancel</ActionBtn>
              </InlineForm>
            )}
          </MessageCard>
        ))}
      </Stack>
    </OverlayContainer>
  );
};

export default InAppMessageOverlay;


